<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

import Button from '@/components/Button.vue'
import Modal from '@/components/Modal.vue'
import NextInput from '@/components/Form/NextInput.vue'
import { useAlert } from '@/composables/useToast'
import { getTags, createTag, updateTag, deleteTag } from '@/api/tags'
import {
  getCannedResponses,
  createCannedResponse,
  updateCannedResponse,
  deleteCannedResponse,
} from '@/api/cannedResponses'

const store = useStore()
const { t } = useI18n()

const currentUser = computed(() => store.getters['auth/getCurrentUser'])
const accountId = computed(() => currentUser.value?.accounts?.[0]?.id)
const hasPermission = computed(() => store.getters['auth/hasPermission'])
const canManageShared = computed(() => hasPermission.value('canned_shared_manage'))

const activeTab = ref('canned')
const busy = ref(false)

const cannedResponses = ref([])
const tags = ref([])

const loadAll = async () => {
  if (!accountId.value) return
  const [cannedResult, tagsResult] = await Promise.all([
    getCannedResponses(accountId.value),
    getTags(accountId.value),
  ])
  cannedResponses.value = cannedResult.data
  tags.value = tagsResult.data
}

// The profile arrives asynchronously, so the first load waits for the account
watch(accountId, id => id && loadAll(), { immediate: true })

// Shared responses first: they are the common script for the whole team
const sortedResponses = computed(() =>
  [...cannedResponses.value].sort((a, b) => {
    if (a.shared !== b.shared) return a.shared ? -1 : 1
    return a.title.localeCompare(b.title)
  })
)

const canEditResponse = response => !response.shared || canManageShared.value

const responseModalOpen = ref(false)
const responseForm = ref({ id: null, title: '', content: '', shared: false })

const openResponseForm = (response = null) => {
  responseForm.value = response
    ? { ...response }
    : { id: null, title: '', content: '', shared: canManageShared.value }
  responseModalOpen.value = true
}

const saveResponse = async () => {
  const { id, title, content, shared } = responseForm.value
  if (!title.trim() || !content.trim()) return
  busy.value = true
  try {
    if (id) await updateCannedResponse(accountId.value, id, { title, content })
    else await createCannedResponse(accountId.value, { title, content, shared })
    await loadAll()
    responseModalOpen.value = false
    useAlert(t('DIRECTORY.CANNED.SAVED'))
  } catch (error) {
    useAlert(t('DIRECTORY.CANNED.ERROR'))
  } finally {
    busy.value = false
  }
}

// Deletion is confirmed in a modal, the same way as in the other settings
const deleteTarget = ref(null)

const confirmDelete = async () => {
  const target = deleteTarget.value
  busy.value = true
  try {
    if (target.type === 'canned') {
      await deleteCannedResponse(accountId.value, target.record.id)
      await loadAll()
    } else {
      const { data } = await deleteTag(accountId.value, target.record.id)
      tags.value = data
    }
    deleteTarget.value = null
  } catch (error) {
    useAlert(
      target.type === 'canned'
        ? t('DIRECTORY.CANNED.ERROR')
        : t('DIRECTORY.TAGS.ERROR')
    )
  } finally {
    busy.value = false
  }
}

const newTagName = ref('')
const editingTagId = ref(null)
const editingTagName = ref('')

const addTag = async () => {
  const name = newTagName.value.trim()
  if (!name) return
  busy.value = true
  try {
    const { data } = await createTag(accountId.value, name)
    tags.value = data
    newTagName.value = ''
  } catch (error) {
    useAlert(t('DIRECTORY.TAGS.ERROR'))
  } finally {
    busy.value = false
  }
}

const startTagEdit = tag => {
  if (!canManageShared.value) return
  editingTagId.value = tag.id
  editingTagName.value = tag.name
}

const saveTagEdit = async () => {
  const name = editingTagName.value.trim()
  const id = editingTagId.value
  editingTagId.value = null
  if (!name) return
  busy.value = true
  try {
    const { data } = await updateTag(accountId.value, id, name)
    tags.value = data
  } catch (error) {
    useAlert(t('DIRECTORY.TAGS.ERROR'))
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="flex flex-col w-full h-full px-6 pt-4 pb-8 overflow-auto bg-n-surface-1">
    <div class="flex flex-col w-full gap-6 mx-auto max-w-5xl font-inter">
      <div class="flex flex-col gap-2">
        <h1 class="text-heading-1 text-n-slate-12">
          {{ $t('DIRECTORY.TITLE') }}
        </h1>
        <p class="text-body-main text-n-slate-11">
          {{ $t('DIRECTORY.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex items-center gap-1 border-b border-n-weak">
        <button
          v-for="tab in ['canned', 'tags']"
          :key="tab"
          type="button"
          class="flex items-center gap-2 px-3 py-2 -mb-px text-sm border-b-2"
          :class="
            activeTab === tab
              ? 'border-n-brand text-n-slate-12 font-medium'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = tab"
        >
          {{
            tab === 'canned'
              ? $t('DIRECTORY.CANNED.TAB')
              : $t('DIRECTORY.TAGS.TAB')
          }}
          <span class="px-1.5 rounded-full text-xs bg-n-alpha-2 text-n-slate-11">
            {{ tab === 'canned' ? cannedResponses.length : tags.length }}
          </span>
        </button>
      </div>

      <!-- Готовые ответы -->
      <section v-if="activeTab === 'canned'" class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-3">
          <p class="text-body-main text-n-slate-11">
            {{ $t('DIRECTORY.CANNED.HINT') }}
          </p>
          <Button
            sm
            icon="i-lucide-plus"
            :label="$t('DIRECTORY.CANNED.ADD')"
            @click="openResponseForm()"
          />
        </div>

        <p
          v-if="!sortedResponses.length"
          class="py-6 text-center text-body-main text-n-slate-11"
        >
          {{ $t('DIRECTORY.CANNED.EMPTY') }}
        </p>

        <ul class="flex flex-col gap-2 p-0 m-0 list-none">
          <li
            v-for="response in sortedResponses"
            :key="response.id"
            class="flex items-start gap-3 p-3 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
          >
            <div class="flex flex-col min-w-0 gap-1 grow">
              <div class="flex items-center gap-2">
                <span class="text-heading-3 text-n-slate-12 truncate">
                  {{ response.title }}
                </span>
                <span
                  class="px-1.5 py-0.5 text-xs rounded-md"
                  :class="
                    response.shared
                      ? 'bg-n-teal-9/10 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  {{
                    response.shared
                      ? $t('DIRECTORY.CANNED.SHARED')
                      : $t('DIRECTORY.CANNED.PERSONAL')
                  }}
                </span>
              </div>
              <p class="text-body-main text-n-slate-11 line-clamp-2">
                {{ response.content }}
              </p>
            </div>
            <div v-if="canEditResponse(response)" class="flex items-center gap-1 shrink-0">
              <Button
                sm
                ghost
                slate
                icon="i-lucide-pencil"
                :aria-label="$t('DIRECTORY.CANNED.EDIT')"
                :disabled="busy"
                @click="openResponseForm(response)"
              />
              <Button
                sm
                ghost
                ruby
                icon="i-lucide-trash-2"
                :aria-label="$t('DIRECTORY.CANNED.DELETE')"
                :disabled="busy"
                @click="deleteTarget = { type: 'canned', record: response }"
              />
            </div>
          </li>
        </ul>
      </section>

      <!-- Теги -->
      <section v-else class="flex flex-col gap-3">
        <p class="text-body-main text-n-slate-11">
          {{
            canManageShared
              ? $t('DIRECTORY.TAGS.HINT')
              : $t('DIRECTORY.TAGS.HINT_READONLY')
          }}
        </p>

        <form
          v-if="canManageShared"
          class="flex items-end gap-2 max-w-lg"
          @submit.prevent="addTag"
        >
          <div class="grow">
            <NextInput
              v-model="newTagName"
              name="new_tag"
              type="text"
              :label="$t('DIRECTORY.TAGS.NAME')"
              :placeholder="$t('DIRECTORY.TAGS.PLACEHOLDER')"
            />
          </div>
          <Button
            type="submit"
            :label="$t('DIRECTORY.TAGS.ADD')"
            :disabled="busy || !newTagName.trim()"
          />
        </form>

        <p
          v-if="!tags.length"
          class="py-6 text-center text-body-main text-n-slate-11"
        >
          {{ $t('DIRECTORY.TAGS.EMPTY') }}
        </p>

        <ul class="flex flex-wrap gap-2 p-0 m-0 list-none">
          <li
            v-for="tag in tags"
            :key="tag.id"
            class="flex items-center gap-2 px-3 h-8 rounded-full outline outline-1 outline-n-weak bg-n-solid-1"
          >
            <input
              v-if="editingTagId === tag.id"
              v-model="editingTagName"
              class="h-6 px-1 text-sm bg-transparent border-none outline-none text-n-slate-12"
              autofocus
              @keyup.enter="saveTagEdit"
              @keyup.esc="editingTagId = null"
              @blur="saveTagEdit"
            />
            <button
              v-else
              type="button"
              class="text-sm bg-transparent border-0 text-n-slate-12"
              :class="canManageShared ? 'cursor-text' : 'cursor-default'"
              @click="startTagEdit(tag)"
            >
              {{ tag.name }}
            </button>
            <button
              v-if="canManageShared"
              type="button"
              class="grid bg-transparent border-0 cursor-pointer place-content-center text-n-slate-10 hover:text-n-ruby-11"
              :aria-label="$t('DIRECTORY.TAGS.DELETE')"
              :disabled="busy"
              @click="deleteTarget = { type: 'tag', record: tag }"
            >
              <span class="i-lucide-x size-3.5" />
            </button>
          </li>
        </ul>
      </section>
    </div>

    <Modal
      :show="Boolean(deleteTarget)"
      :title="
        deleteTarget?.type === 'tag'
          ? $t('DIRECTORY.TAGS.DELETE')
          : $t('DIRECTORY.CANNED.DELETE')
      "
      @close="deleteTarget = null"
    >
      <div class="flex flex-col gap-6 p-6">
        <p class="text-body-main text-n-slate-11">
          {{
            deleteTarget?.type === 'tag'
              ? $t('DIRECTORY.TAGS.CONFIRM_DELETE', {
                  name: deleteTarget?.record.name,
                })
              : $t('DIRECTORY.CANNED.CONFIRM_DELETE', {
                  title: deleteTarget?.record.title,
                })
          }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            faded
            slate
            :label="$t('DIRECTORY.CANCEL')"
            @click="deleteTarget = null"
          />
          <Button
            ruby
            :label="$t('DIRECTORY.DELETE')"
            :disabled="busy"
            @click="confirmDelete"
          />
        </div>
      </div>
    </Modal>

    <Modal
      :show="responseModalOpen"
      :title="
        responseForm.id
          ? $t('DIRECTORY.CANNED.EDIT')
          : $t('DIRECTORY.CANNED.ADD')
      "
      @close="responseModalOpen = false"
    >
      <form class="flex flex-col gap-4 p-6" @submit.prevent="saveResponse">
        <NextInput
          v-model="responseForm.title"
          name="canned_title"
          type="text"
          :label="$t('DIRECTORY.CANNED.FORM_TITLE')"
        />
        <div class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('DIRECTORY.CANNED.FORM_CONTENT') }}
          </span>
          <textarea
            v-model="responseForm.content"
            rows="6"
            class="w-full p-3 text-sm border-none resize-y rounded-xl outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
          />
        </div>
        <label
          v-if="canManageShared && !responseForm.id"
          class="flex items-center gap-2 text-body-main text-n-slate-12 cursor-pointer"
        >
          <input
            v-model="responseForm.shared"
            type="checkbox"
            class="w-4 h-4 accent-n-brand"
          />
          {{ $t('DIRECTORY.CANNED.SHARED_HINT') }}
        </label>
        <div class="flex justify-end gap-2">
          <Button
            faded
            slate
            type="button"
            :label="$t('DIRECTORY.CANCEL')"
            @click="responseModalOpen = false"
          />
          <Button
            type="submit"
            :label="$t('DIRECTORY.SAVE')"
            :disabled="busy"
          />
        </div>
      </form>
    </Modal>
  </div>
</template>
